import { clearAllStorageData, getAuthData, getAuthToken, setStoredAuthData } from '../utils/authStorage.js';

/**
 * API Service utility for handling HTTP requests
 */

// API Base URL from environment or default to empty string for Vite proxy
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * HTTP client wrapper with common configurations
 */
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.isRedirectingForAuth = false;
  }

  /**
   * Get authentication token from localStorage
   */
  getAuthToken() {
    return getAuthToken();
  }

  /**
   * Common headers for API requests
   */
  getHeaders(includeAuth = false, contentType = 'application/json') {
    const headers = {};

    // Only set Content-Type if specified (don't set for FormData)
    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Clear stored auth and redirect to login immediately.
   * This is used when a protected request cannot be recovered by token refresh.
   */
  handleUnauthorizedRedirect() {
    if (this.isRedirectingForAuth) {
      return;
    }

    this.isRedirectingForAuth = true;

    clearAllStorageData();

    const publicPaths = ['/', '/login', '/signup', '/forgot-password', '/verify-code', '/two-factor-auth'];

    if (typeof window !== 'undefined' && !publicPaths.includes(window.location.pathname)) {
      window.location.replace('/login');
    }
  }

  /**
   * Generic fetch wrapper with error handling and token refresh
   */
  async request(endpoint, options = {}) {
    let url = `${this.baseURL}${endpoint}`;

    // Add query parameters if provided
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
      if (searchParams.toString()) {
        url += `?${searchParams.toString()}`;
      }
    }

    // Determine if this is FormData
    const isFormData = options.body instanceof FormData;
    const contentType = isFormData ? null : 'application/json';

    const config = {
      headers: {
        ...this.getHeaders(options.requireAuth || false, contentType),
        ...options.headers
      },
      ...options
    };

    try {
      let response = await fetch(url, config);

      // Handle token refresh for 401 errors
      if (response.status === 401 && options.requireAuth && !options._isRetry) {
        try {
          // Refresh token directly to avoid circular dependency with authService
          const authData = getAuthData();
          
          if (authData.refreshToken) {
            // Make refresh request directly using fetch to avoid circular dependency
            const refreshUrl = `${this.baseURL}/Auth/refresh`;
            const refreshResponse = await fetch(refreshUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                refreshToken: authData.refreshToken
              })
            });

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              
              if (refreshData.isSuccess && refreshData.token) {
                // Update stored token
                authData.token = refreshData.token;
                if (refreshData.refreshToken) {
                  authData.refreshToken = refreshData.refreshToken;
                }
                setStoredAuthData(authData);

                // Retry the original request with new token
                const newConfig = {
                  ...config,
                  headers: this.getHeaders(true),
                  _isRetry: true
                };

                response = await fetch(url, newConfig);
              }
            }
          }
        } catch {
          this.handleUnauthorizedRedirect();
        }
      }

      // Handle different response types
      let data;
      const contentType = response.headers.get('Content-Type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        if (response.status === 401 && options.requireAuth) {
          this.handleUnauthorizedRedirect();
        }

        // Parse validation errors for better error messages
        let errorMessage = data?.message || `HTTP error! status: ${response.status}`;

        if (data?.errors && typeof data.errors === 'object') {
          const validationErrors = Object.entries(data.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = { status: response.status, data };
        throw error;
      }

      return data;
    } catch (error) {
      // Enhance error object with more details
      if (!error.status) {
        // Network or other fetch errors
        error.status = 0;
        error.isNetworkError = true;
      }

      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...options
    });
  }

  /**
   * POST request
   */
  async post(endpoint, body = null, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : null,
      ...options
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, body = null, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : null,
      ...options
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, body = null, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : null,
      ...options
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
